import React from "react";
import Breadcrumb from "../../components/breadcrumb";
import { useToast } from "../../components/toast/toast-context";
import UQSpacer from "../../components/spacer";
import UQCode from "../../components/code";
import UQDataGrid, { UQDataGridColumn } from "../../components/data-grid";
import UQButton from "../../components/button";
import { BREADCRUMB_ROOT } from "../../common/config";
import UQGrid from "../../components/grid";

const ToastPage = () => {
  const { addToast } = useToast();

  const handleShowToast = () => {
    addToast({
      type: "success",
      title: "Success",
      content: "This is a success toast!",
      duration: 5000,
    });
  };

  const attributes = [
    {
      attribute: "id",
      type: "string",
      default: "undefined",
      description: "A unique identifier for the toast.",
    },
    {
      attribute: "type",
      type: "'error' | 'warning' | 'info' | 'success'",
      default: "'info'",
      description: "The type of the toast, which determines its style.",
    },
    {
      attribute: "title",
      type: "string",
      default: "undefined",
      description: "The title of the toast.",
    },
    {
      attribute: "content",
      type: "string",
      default: "undefined",
      description: "The content of the toast.",
    },
    {
      attribute: "duration",
      type: "number",
      default: "undefined",
      description: "The duration (in milliseconds) before the toast disappears. If undefined, the toast stays until manually closed.",
    },
    {
      attribute: "position",
      type: "'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'",
      default: "'bottom-left'",
      description: "The position of the toast on the screen.",
    },
    {
      attribute: "onClose",
      type: "(id: string) => void",
      default: "undefined",
      description: "Callback function triggered when the toast is closed.",
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: "attribute",
      renderHeaderCell: () => "Attribute",
      renderCell: (item) => item.attribute,
    },
    {
      columnId: "type",
      renderHeaderCell: () => "Type",
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: "default",
      renderHeaderCell: () => "Default",
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: "description",
      renderHeaderCell: () => "Description",
      renderCell: (item) => item.description,
    },
  ];

  const toastCodeSnippet = `
import React from 'react';
import { useToast } from '@uniqconsulting/ui';

const MyToastExample = () => {
  const { addToast } = useToast();

  return (
    <div>
      <UQButton type="primary" onClick={() => addToast({ type: 'success', content: 'This is a success toast!', duration: 5000 })}>
        Show Success Toast
      </UQButton>
    </div>
  );
};
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, "Toast"]} />
      <h1>Toast Demo</h1>
      <UQSpacer size="m" />
      <UQButton type="primary" onClick={handleShowToast}>
        Show Toast
      </UQButton>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQToast</code> component is used to display temporary messages to the user. It supports different types (error, warning, info, success) and can be configured to disappear after a specified duration or stay until manually closed.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Live Demo */}
      <h2>Live Demo</h2>
      <UQSpacer size="s" />
      <UQGrid>
        <UQButton type="primary" onClick={() => addToast({ type: "success", content: "This is a success toast!", position: "bottom-left", duration: 5000 })}>
          Show Success Toast (5s Timer)
        </UQButton>
        <UQButton type="primary" onClick={() => addToast({ type: "error", content: "This is an error toast!" })}>
          Show Error Toast (Manual Close)
        </UQButton>
        <UQButton type="primary" onClick={() => addToast({ type: "warning", content: "This is a warning toast!", position: "top-left" })}>
          Show Warning Toast (Top Left)
        </UQButton>
        <UQButton type="primary" onClick={() => addToast({ type: "info", content: "This is an info toast!", position: "top-right" })}>
          Show Info Toast (Top Right)
        </UQButton>
      </UQGrid>
      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <UQSpacer size="s" />
      <UQCode code={toastCodeSnippet} enableCopy />
    </>
  );
};

export default ToastPage;
